import {
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  increment,
  limit,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "./firebase";
import { SEED_RECIPES } from "./seedRecipes";
import { computeRevisionFields } from "./recipeDiff";
import { slugify } from "./format";
import { withImage } from "./recipeImage";
import type {
  Connection,
  MealSlot,
  PassMode,
  PlanDay,
  PlannedMeal,
  PublicProfile,
  UserPreferences,
  Recipe,
  RecipeSend,
  Revision,
  SharedRecipe,
} from "./types";

// ── My cookbook ─────────────────────────────────────────────────────
// Both write paths that overwrite the live recipe doc (updateRecipe,
// restoreRevision) would otherwise drop an archived flag on the floor, since
// neither the chatbot's recipe objects nor old revision snapshots carry one.
// Returns the fields to merge back in, omitting undefined (Firestore rejects it).
function shelfState(prev: Partial<Recipe>): Partial<Recipe> {
  if (!prev.archived) return {};
  return { archived: true, ...(prev.archivedAt ? { archivedAt: prev.archivedAt } : {}) };
}

// Same problem, same shape, for where a recipe came from: a copy or a fork
// carries its lineage on the live doc only, so the first edit — which arrives
// as a whole recipe object that has never heard of it — would otherwise erase
// the link back to the original. Merged back in by every full-document write.
function lineage(prev: Partial<Recipe>): Partial<Recipe> {
  return {
    ...(prev.copiedFrom ? { copiedFrom: prev.copiedFrom } : {}),
    ...(prev.copiedAt ? { copiedAt: prev.copiedAt } : {}),
    ...(prev.forkedFrom ? { forkedFrom: prev.forkedFrom } : {}),
    ...(prev.forkedAt ? { forkedAt: prev.forkedAt } : {}),
  };
}

// Fields that live on the recipe doc but are not the recipe: bookkeeping,
// shelf state, and lineage. Stripped on the way into every revision snapshot
// so history stays pure content — mirrors _save_revision in mcp/main.py.
function contentOnly(recipe: Partial<Recipe>): Partial<Recipe> {
  const {
    id: _id,
    currentRevisionNumber: _rev,
    archived: _ar,
    archivedAt: _at,
    copiedFrom: _cf,
    copiedAt: _ca,
    forkedFrom: _ff,
    forkedAt: _fa,
    ...content
  } = recipe;
  return content;
}

// The recipe as a reader of a share sees it: content, plus the two bookkeeping
// fields a viewer needs to resolve revisions. Lineage is the author's own
// note-to-self — publishing a copy shouldn't announce which of your other
// recipes it came from, and a fork of it shouldn't inherit that either.
function shareSnapshot(recipe: Recipe): Recipe {
  return {
    ...contentOnly(recipe),
    id: recipe.id,
    ...(recipe.currentRevisionNumber != null
      ? { currentRevisionNumber: recipe.currentRevisionNumber }
      : {}),
  } as Recipe;
}

// Create a new recipe in the user's cookbook. Mirrors mcp/main.py create_recipe:
// rejects duplicate ids, writes the recipe, stamps an initial revision (#1),
// and points currentRevisionNumber at it so the Recipe page renders correctly.
export async function createRecipe(uid: string, recipe: Recipe): Promise<void> {
  const recipeRef = doc(db, "users", uid, "recipes", recipe.id);
  if ((await getDoc(recipeRef)).exists()) {
    throw new Error(`A recipe with id "${recipe.id}" already exists in your cookbook.`);
  }
  const snapshot = withImage(contentOnly(recipe), recipe.id);
  await setDoc(recipeRef, { ...snapshot, currentRevisionNumber: 1 });
  await addDoc(collection(db, "users", uid, "recipes", recipe.id, "revisions"), {
    number: 1,
    snapshot,
    savedAt: new Date().toISOString(),
    note: "Initial",
  });
}

// Overwrite an existing recipe in place and record a new revision. Mirrors
// mcp/main.py update_recipe but takes a full Recipe (the chatbot emits complete
// recipes, not partial patches). Bumps currentRevisionNumber so the Recipe page
// stays in sync. Does NOT prune old revisions — the MCP server's periodic
// pruner handles that.
export async function updateRecipe(
  uid: string,
  recipeId: string,
  recipe: Recipe,
  note: string = "Updated"
): Promise<number> {
  const recipeRef = doc(db, "users", uid, "recipes", recipeId);
  const existing = await getDoc(recipeRef);
  if (!existing.exists()) {
    throw new Error(`Recipe "${recipeId}" not found in your cookbook.`);
  }
  const revsCol = collection(db, "users", uid, "recipes", recipeId, "revisions");
  const revsSnap = await getDocs(revsCol);
  const nextNumber = revsSnap.docs.reduce(
    (m, d) => Math.max(m, (d.data() as { number?: number }).number ?? 0),
    0
  ) + 1;
  // Archive state is shelf status and lineage is provenance — neither is
  // content, so neither enters a revision snapshot, and both survive an edit
  // even when the caller (the chatbot, say) hands us a fresh recipe object
  // that has never heard of them.
  const snapshot = withImage(contentOnly(recipe), recipeId);
  const prevData = existing.data() as Partial<Recipe>;
  const fields = computeRevisionFields(prevData, snapshot);
  const savedAt = new Date().toISOString();
  await setDoc(recipeRef, {
    ...snapshot,
    currentRevisionNumber: nextNumber,
    ...shelfState(prevData),
    ...lineage(prevData),
  });
  await addDoc(revsCol, {
    number: nextNumber,
    snapshot,
    fields,
    savedAt,
    note,
  });

  // If this recipe is already shared (Kitchen Table or a link/pass), keep the
  // shared artifact in sync: live links/passes follow latestSnapshot, and the
  // just-saved revision becomes pinnable in exposedRevisions.
  const shareRef = doc(db, "sharedRecipes", `${uid}_${recipeId}`);
  const shareSnap = await getDoc(shareRef);
  if (shareSnap.exists()) {
    const allRevs: Revision[] = [
      ...revsSnap.docs.map((d) => ({ _docId: d.id, ...(d.data() as Omit<Revision, "_docId">) })),
      { _docId: "_new", number: nextNumber, snapshot: snapshot as Recipe, savedAt, note },
    ];
    await updateDoc(shareRef, {
      latestSnapshot: { ...snapshot, id: recipeId, currentRevisionNumber: nextNumber },
      currentRevisionNumber: nextNumber,
      title: snapshot.title,
      exposedRevisions: buildExposedAll(allRevs),
      updatedAt: savedAt,
    });
  }
  return nextNumber;
}

// Copy one of your own recipes into a brand-new one — the "try it a different
// way without touching the original" move, and the private-side twin of forking
// someone else's off the Kitchen Table.
//
// The copy is a genuinely separate recipe, not a branch: its own id, its own
// revision history starting at #1, and none of the original's shares, links, or
// archive state. What the two share is a lineage stamp, so a copy made in a
// hurry can still be traced back. Mirrors mcp/main.py copy_recipe.
export async function copyRecipe(uid: string, source: Recipe, title?: string): Promise<string> {
  const newTitle = title?.trim() || `${source.title} (copy)`;
  const newId = await freeRecipeId(uid, slugify(newTitle) || `${source.id}-copy`);
  // Keyed on the source's id, not the copy's: a copy of a photo-less recipe
  // should look like the recipe it came from, not draw a different picture.
  const content = withImage({ ...contentOnly(source), title: newTitle }, source.id);
  const now = new Date().toISOString();
  await setDoc(doc(db, "users", uid, "recipes", newId), {
    ...content,
    currentRevisionNumber: 1,
    copiedFrom: {
      recipeId: source.id,
      title: source.title,
      ...(source.currentRevisionNumber != null
        ? { revisionNumber: source.currentRevisionNumber }
        : {}),
    },
    copiedAt: now,
  });
  await addDoc(collection(db, "users", uid, "recipes", newId, "revisions"), {
    number: 1,
    snapshot: content,
    savedAt: now,
    note: `Copied from "${source.title}"`,
  });
  return newId;
}

// First free slug in the base, base-2, base-3 … series. Copying is meant to be
// one click, so the copy names itself instead of making you invent an id up
// front. Bounded rather than looping: if fifty are taken, something is wrong
// and the cook should pick a title.
async function freeRecipeId(uid: string, base: string): Promise<string> {
  for (let n = 1; n <= 50; n++) {
    const candidate = n === 1 ? base : `${base}-${n}`;
    if (!(await getDoc(doc(db, "users", uid, "recipes", candidate))).exists()) return candidate;
  }
  throw new Error("Couldn't find a free id for the copy — give it a different title.");
}

// Archive a recipe (or bring it back). Archiving is deliberately not a delete:
// the doc, its revisions, and any share it backs are all untouched — it simply
// stops appearing on the cookbook's shelves and in the meal-planner picker.
// Anything that already points AT the recipe (a planned meal, a public link)
// keeps resolving, which is why nothing here cascades.
export async function setRecipeArchived(
  uid: string,
  recipeId: string,
  archived: boolean
): Promise<void> {
  await updateDoc(doc(db, "users", uid, "recipes", recipeId), {
    archived,
    archivedAt: archived ? new Date().toISOString() : deleteField(),
  });
}

export async function listMyRecipes(uid: string): Promise<Recipe[]> {
  const snap = await getDocs(
    query(collection(db, "users", uid, "recipes"), orderBy("category"))
  );
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Recipe, "id">) }));
}

export async function getMyRecipe(uid: string, id: string): Promise<Recipe | null> {
  const snap = await getDoc(doc(db, "users", uid, "recipes", id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...(snap.data() as Omit<Recipe, "id">) };
}

// Delete a recipe along with its revisions subcollection and any community
// share doc. Firestore does not cascade subcollection deletes, so we sweep
// revisions explicitly. The share doc may not exist; ignore "not found".
export async function deleteRecipe(uid: string, recipeId: string): Promise<void> {
  const revsCol = collection(db, "users", uid, "recipes", recipeId, "revisions");
  const revsSnap = await getDocs(revsCol);
  if (revsSnap.size > 0) {
    const batch = writeBatch(db);
    revsSnap.docs.forEach((d) => batch.delete(d.ref));
    await batch.commit();
  }
  await deleteDoc(doc(db, "sharedRecipes", `${uid}_${recipeId}`)).catch(() => {});
  await deleteDoc(doc(db, "users", uid, "recipes", recipeId));
}

// Idempotent: writes the default seed recipes for a new user on first sign-in.
// Guarded by a seededAt stamp on the user doc so deleting a seed won't bring it back.
// Per-recipe getDoc check protects against partial-failure replays during the same run.
export async function seedRecipesIfNeeded(uid: string): Promise<void> {
  const userRef = doc(db, "users", uid);
  const userSnap = await getDoc(userRef);
  if (userSnap.exists() && (userSnap.data() as { seededAt?: unknown }).seededAt) return;
  for (const seed of SEED_RECIPES) {
    const { id } = seed;
    const recipeRef = doc(db, "users", uid, "recipes", id);
    const existing = await getDoc(recipeRef);
    if (existing.exists()) continue;
    await setDoc(recipeRef, seed);
  }
  await setDoc(userRef, { seededAt: new Date().toISOString() }, { merge: true });
}

export async function listRevisions(uid: string, recipeId: string): Promise<Revision[]> {
  const snap = await getDocs(
    query(
      collection(db, "users", uid, "recipes", recipeId, "revisions"),
      orderBy("number", "desc")
    )
  );
  return snap.docs.map((d) => ({ _docId: d.id, ...(d.data() as Omit<Revision, "_docId">) }));
}

// Restore the recipe to an earlier revision's snapshot. Mirrors mcp/main.py
// restore_revision: writes the old snapshot to the live doc, then records a
// NEW revision so the previous live state is preserved.
export async function restoreRevision(
  uid: string,
  recipeId: string,
  targetNumber: number
): Promise<number> {
  const revsCol = collection(db, "users", uid, "recipes", recipeId, "revisions");
  const allSnap = await getDocs(revsCol);
  const all = allSnap.docs.map((d) => ({ _docId: d.id, ...(d.data() as Omit<Revision, "_docId">) }));
  const target = all.find((r) => r.number === targetNumber);
  if (!target || !target.snapshot) {
    throw new Error(`Revision ${targetNumber} not found or has no snapshot.`);
  }
  const recipeRef = doc(db, "users", uid, "recipes", recipeId);
  // Old snapshots can predate the photo rule — restoring one shouldn't put
  // the recipe back to having no picture.
  const snapshot = withImage(contentOnly((target.snapshot as Recipe) ?? {}), recipeId);
  // Diff the current live state (before we overwrite it) against the snapshot
  // we're restoring, so the new revision carries a "WHAT CHANGED" diff just like
  // a normal edit. Without this, restored revisions show "No structured diff
  // captured." Mirrors updateRecipe.
  const liveSnap = await getDoc(recipeRef);
  const prevData = (liveSnap.exists() ? liveSnap.data() : {}) as Partial<Recipe>;
  const fields = computeRevisionFields(prevData, snapshot);
  await setDoc(recipeRef, { ...snapshot, ...shelfState(prevData), ...lineage(prevData) });
  const newNumber = (all.reduce((m, r) => Math.max(m, r.number ?? 0), 0)) + 1;
  await addDoc(revsCol, {
    number: newNumber,
    snapshot,
    fields,
    savedAt: new Date().toISOString(),
    note: `Restored from revision ${targetNumber}`,
  });
  await updateDoc(recipeRef, { currentRevisionNumber: newNumber });
  return newNumber;
}

export async function setRevisionMilestone(
  uid: string,
  recipeId: string,
  revDocId: string,
  releaseName: string | null
) {
  const ref = doc(db, "users", uid, "recipes", recipeId, "revisions", revDocId);
  await updateDoc(ref, {
    releaseName: releaseName ? releaseName : deleteField(),
  });
}

export async function updateRevisionNote(
  uid: string,
  recipeId: string,
  revDocId: string,
  note: string
) {
  const ref = doc(db, "users", uid, "recipes", recipeId, "revisions", revDocId);
  await updateDoc(ref, { note });
}

// How a version actually turned out when it was cooked: stars and a personal
// note, both optional and independently clearable. Mirrors mcp/main.py
// rate_revision — same two fields on the same revision doc, so a verdict left
// from the chat assistant and one left on the page are the same thing.
// Deliberately a partial update: it never touches the snapshot or the change
// note, so rating an old revision doesn't rewrite history.
export async function setRevisionFeedback(
  uid: string,
  recipeId: string,
  revDocId: string,
  feedback: { rating?: number | null; outcome?: string | null }
) {
  const ref = doc(db, "users", uid, "recipes", recipeId, "revisions", revDocId);
  const patch: Record<string, unknown> = {};
  if (feedback.rating !== undefined) {
    patch.rating = feedback.rating ? feedback.rating : deleteField();
  }
  if (feedback.outcome !== undefined) {
    const trimmed = feedback.outcome?.trim();
    patch.outcome = trimmed ? trimmed : deleteField();
  }
  if (Object.keys(patch).length === 0) return;
  await updateDoc(ref, patch);
}

export async function deleteRevision(uid: string, recipeId: string, revDocId: string) {
  await deleteDoc(doc(db, "users", uid, "recipes", recipeId, "revisions", revDocId));
}

// Collapse a contiguous range of revisions [fromNumber, toNumber] into a single
// revision. The newest revision in the range is kept (its number, snapshot, and
// timestamp survive); the older ones are deleted. The kept revision's "WHAT
// CHANGED" diff is recomputed against the revision immediately before the range
// so it summarises the whole collapsed span, and its note is replaced.
//
// Numbering is intentionally left with a gap — the rest of the system already
// tolerates gaps (the MCP pruner deletes oldest untagged revisions), and the
// live recipe's currentRevisionNumber always points at the max revision, which
// the kept revision preserves. Tagged releases are permanent milestones, so a
// range containing one is refused rather than collapsed away.
export async function squashRevisions(
  uid: string,
  recipeId: string,
  fromNumber: number,
  toNumber: number,
  note?: string
): Promise<number> {
  if (fromNumber >= toNumber) {
    throw new Error("Select a range of at least two revisions to squash.");
  }
  const revsCol = collection(db, "users", uid, "recipes", recipeId, "revisions");
  const allSnap = await getDocs(revsCol);
  const all = allSnap.docs
    .map((d) => ({ _docId: d.id, ...(d.data() as Omit<Revision, "_docId">) }))
    .sort((a, b) => a.number - b.number);

  const inRange = all.filter((r) => r.number >= fromNumber && r.number <= toNumber);
  if (inRange.length < 2) {
    throw new Error("Need at least two revisions in the selected range to squash.");
  }
  const tagged = inRange.find((r) => r.releaseName);
  if (tagged) {
    throw new Error(
      `Revision #${tagged.number} is tagged "${tagged.releaseName}". Remove the milestone tag before squashing this range.`
    );
  }

  // Keep the newest revision in the range; everything older is folded into it.
  const kept = inRange[inRange.length - 1];
  if (!kept.snapshot) {
    throw new Error(`Revision #${kept.number} has no snapshot and can't anchor a squash.`);
  }
  const { currentRevisionNumber: _drop, ...keptSnapshot } = (kept.snapshot as Recipe) ?? {};

  // Diff against the revision just before the range so the surviving revision's
  // diff covers the entire collapsed span. When the range starts at the earliest
  // revision there is no predecessor — the kept revision becomes the new base,
  // so diff against an empty recipe (full contents shown as added) rather than
  // recording an empty, misleading "no changes" diff.
  const before = all.filter((r) => r.number < fromNumber).pop();
  const base = (before?.snapshot ?? {}) as Partial<Recipe>;
  const fields = computeRevisionFields(base, keptSnapshot);

  const finalNote = note?.trim() || `Squashed revisions ${fromNumber}–${toNumber}`;

  const batch = writeBatch(db);
  batch.update(doc(revsCol, kept._docId), { fields, note: finalNote });
  for (const r of inRange.slice(0, -1)) {
    batch.delete(doc(revsCol, r._docId));
  }
  await batch.commit();

  // If the recipe is shared, exposedRevisions may now reference deleted
  // revisions — rebuild it from what survives. currentRevisionNumber is
  // unchanged (the kept revision preserves the max number).
  const shareRef = doc(db, "sharedRecipes", `${uid}_${recipeId}`);
  const shareSnap = await getDoc(shareRef);
  if (shareSnap.exists()) {
    const deleted = new Set(inRange.slice(0, -1).map((r) => r._docId));
    const remaining = all
      .filter((r) => !deleted.has(r._docId))
      .map((r) => (r._docId === kept._docId ? { ...r, fields, note: finalNote } : r));
    await updateDoc(shareRef, {
      exposedRevisions: buildExposedAll(remaining),
      updatedAt: new Date().toISOString(),
    });
  }
  return kept.number;
}

// ── User preferences ────────────────────────────────────────────────
// Preferences live as a `preferences` map on the user doc (alongside apiKey,
// displayName, etc.), so reading them is a single point read and saving merges
// without clobbering the rest of the user doc.
export async function getPreferences(uid: string): Promise<UserPreferences> {
  const snap = await getDoc(doc(db, "users", uid));
  if (!snap.exists()) return {};
  return (snap.data() as { preferences?: UserPreferences }).preferences ?? {};
}

export async function savePreferences(
  uid: string,
  prefs: UserPreferences
): Promise<void> {
  await setDoc(doc(db, "users", uid), { preferences: prefs }, { merge: true });
}

// ── On Deck (meal planning) ─────────────────────────────────────────
// Fetch the plan for a set of dates (typically the seven days of a week).
// Days with no saved doc are simply absent from the result — the board fills
// them in with empty slots. Reads run in parallel so a week is one round trip.
export async function getPlanDays(
  uid: string,
  dates: string[]
): Promise<Record<string, PlanDay>> {
  const out: Record<string, PlanDay> = {};
  await Promise.all(
    dates.map(async (date) => {
      const snap = await getDoc(doc(db, "users", uid, "onDeck", date));
      if (snap.exists()) {
        out[date] = { date, ...(snap.data() as Omit<PlanDay, "date">) };
      }
    })
  );
  return out;
}

// Persist a single day. A day with no meals and no target carries no
// information, so it is deleted rather than left as an empty husk — keeps the
// collection (and future week reads) tidy.
export async function savePlanDay(uid: string, day: PlanDay): Promise<void> {
  const ref = doc(db, "users", uid, "onDeck", day.date);
  const { date: _date, ...rest } = day;
  if (rest.meals.length === 0 && rest.targetCalories == null) {
    await deleteDoc(ref).catch(() => {});
    return;
  }
  await setDoc(ref, rest);
}

// Tick every meal in one of a day's slots as eaten. Answering a meal-time
// notification lands here, which is why it re-reads the day rather than
// patching client state: the answer can arrive days after the app last had
// this plan in memory, and the meal may have been edited or removed since.
// A slot that's gone is not an error — the question simply no longer applies.
export async function markSlotEaten(
  uid: string,
  date: string,
  slot: MealSlot
): Promise<number> {
  const ref = doc(db, "users", uid, "onDeck", date);
  const snap = await getDoc(ref);
  if (!snap.exists()) return 0;
  const data = snap.data() as Omit<PlanDay, "date">;
  const meals = data.meals ?? [];
  let touched = 0;
  const next = meals.map((m) => {
    if (m.slot !== slot || m.eaten) return m;
    touched++;
    return { ...m, eaten: true };
  });
  if (touched === 0) return 0;
  await setDoc(ref, { ...data, meals: next });
  return touched;
}

// Append one meal to a day, preserving whatever is already planned there.
// Used by "send to a week" on the recipe page; On Deck edits its own state
// optimistically and calls savePlanDay directly.
export async function addPlannedMeal(
  uid: string,
  date: string,
  meal: PlannedMeal
): Promise<PlanDay> {
  const snap = await getDoc(doc(db, "users", uid, "onDeck", date));
  const existing: PlanDay = snap.exists()
    ? { date, ...(snap.data() as Omit<PlanDay, "date">) }
    : { date, meals: [] };
  const next: PlanDay = { ...existing, meals: [...existing.meals, meal] };
  await savePlanDay(uid, next);
  return next;
}

// ── Shopping ────────────────────────────────────────────────────────
// The list itself is always derived from the plan (see lib/shoppingList.ts) —
// only what the cook did to it is stored: which lines are ticked off and any
// extras they added at the store. Keyed by week so a list survives closing the
// app mid-aisle. Doc id is the week's Monday, e.g. shopping/2026-08-03.
export type ShoppingExtra = { id: string; name: string };

export type ShoppingState = {
  checked: string[];        // ShoppingItem keys, plus `extra:<id>` for extras
  extras: ShoppingExtra[];
  updatedAt?: string;
  // Grocery/Reminders sync (see lib/grocerySync.ts). reminderLinks maps an
  // item key to the reminder we created for it, so a re-push updates rather
  // than duplicates, and a tick in Grocery finds its way back to the right
  // line. Absent until the cook syncs this week for the first time.
  reminderListId?: string;
  reminderLinks?: Record<string, string>;
  syncedAt?: string;
};

export async function getShoppingState(
  uid: string,
  listId: string
): Promise<ShoppingState> {
  const snap = await getDoc(doc(db, "users", uid, "shopping", listId));
  if (!snap.exists()) return { checked: [], extras: [] };
  const data = snap.data() as Partial<ShoppingState>;
  return {
    checked: data.checked ?? [],
    extras: data.extras ?? [],
    updatedAt: data.updatedAt,
    ...(data.reminderListId ? { reminderListId: data.reminderListId } : {}),
    ...(data.reminderLinks ? { reminderLinks: data.reminderLinks } : {}),
    ...(data.syncedAt ? { syncedAt: data.syncedAt } : {}),
  };
}

// A list with nothing ticked, no extras and no reminders behind it carries no
// information — drop the doc rather than leave an empty husk, matching
// savePlanDay. Reminder links count: losing them would orphan every reminder
// we made, leaving phantom items in the cook's Grocery list.
export async function saveShoppingState(
  uid: string,
  listId: string,
  state: ShoppingState
): Promise<void> {
  const ref = doc(db, "users", uid, "shopping", listId);
  const hasLinks = Object.keys(state.reminderLinks ?? {}).length > 0;
  if (state.checked.length === 0 && state.extras.length === 0 && !hasLinks) {
    await deleteDoc(ref).catch(() => {});
    return;
  }
  await setDoc(ref, {
    checked: state.checked,
    extras: state.extras,
    updatedAt: new Date().toISOString(),
    ...(state.reminderListId ? { reminderListId: state.reminderListId } : {}),
    ...(hasLinks ? { reminderLinks: state.reminderLinks } : {}),
    ...(state.syncedAt ? { syncedAt: state.syncedAt } : {}),
  });
}

// ── Sharing / community ─────────────────────────────────────────────
export async function getShareForRecipe(uid: string, id: string): Promise<SharedRecipe | null> {
  const snap = await getDoc(doc(db, "sharedRecipes", `${uid}_${id}`));
  if (!snap.exists()) return null;
  return { shareId: snap.id, ...(snap.data() as Omit<SharedRecipe, "shareId">) };
}

export async function getShared(shareId: string): Promise<SharedRecipe | null> {
  const snap = await getDoc(doc(db, "sharedRecipes", shareId));
  if (!snap.exists()) return null;
  return { shareId: snap.id, ...(snap.data() as Omit<SharedRecipe, "shareId">) };
}

export async function listShared(): Promise<SharedRecipe[]> {
  const snap = await getDocs(
    query(collection(db, "sharedRecipes"), orderBy("publishedAt", "desc"), limit(200))
  );
  return snap.docs
    .map((d) => ({ shareId: d.id, ...(d.data() as Omit<SharedRecipe, "shareId">) }))
    // Link/pass-only shares are readable by id but never listed on the table.
    // Legacy docs have no visibility field and stay public.
    .filter((s) => s.visibility !== "private");
}

export async function publishRecipe(
  uid: string,
  recipeId: string,
  payload: Omit<SharedRecipe, "shareId" | "ownerId" | "originalRecipeId">
): Promise<string> {
  const shareId = `${uid}_${recipeId}`;
  const ref = doc(db, "sharedRecipes", shareId);
  const existing = await getDoc(ref);
  const clean = payload.latestSnapshot
    ? { ...payload, latestSnapshot: shareSnapshot(payload.latestSnapshot) }
    : payload;
  if (existing.exists()) {
    // May be promoting a private link/pass share onto the table — mark it public.
    await updateDoc(ref, { ...clean, visibility: "public", updatedAt: new Date().toISOString() });
  } else {
    await setDoc(ref, {
      ...clean,
      ownerId: uid,
      originalRecipeId: recipeId,
      visibility: "public",
      forkCount: 0,
      commentCount: 0,
      publishedAt: new Date().toISOString(),
    });
  }
  return shareId;
}

// "Unpublish" delists the recipe from the Kitchen Table but keeps the share doc
// so any public links or passes that point at it keep working. The doc is only
// fully removed when the recipe itself is deleted (see deleteRecipe).
export async function unpublishRecipe(uid: string, recipeId: string) {
  const ref = doc(db, "sharedRecipes", `${uid}_${recipeId}`);
  if ((await getDoc(ref)).exists()) {
    await updateDoc(ref, { visibility: "private", updatedAt: new Date().toISOString() });
  }
}

// One-shot backfill: stamp the owner's display name onto an already-published
// share doc that pre-dates the ownerName field. Safe to call repeatedly — only
// writes when the field is actually missing.
export async function backfillShareOwnerName(
  uid: string,
  recipeId: string,
  ownerName: string
): Promise<void> {
  const ref = doc(db, "sharedRecipes", `${uid}_${recipeId}`);
  await updateDoc(ref, { ownerName });
}

// Build the exposedRevisions payload from a recipe's full revision list,
// dropping the current revision (already covered by latestSnapshot) and any
// missing snapshots (nothing to render). Shape matches SharedRecipe.exposedRevisions.
// Fields are listed one by one on purpose: rating and outcome are the cook's
// private verdict on a version and must never ride along into a public share.
export function buildExposedRevisions(
  revs: Revision[],
  currentRevisionNumber: number | undefined
): NonNullable<SharedRecipe["exposedRevisions"]> {
  return revs
    .filter((r) => r.number !== currentRevisionNumber && r.snapshot)
    .sort((a, b) => b.number - a.number)
    .map((r) => ({
      number: r.number,
      ...(r.note ? { note: r.note } : {}),
      ...(r.releaseName ? { releaseName: r.releaseName } : {}),
      ...(r.savedAt ? { savedAt: r.savedAt } : {}),
      ...(r.snapshot ? { snapshot: r.snapshot } : {}),
    }));
}

// Like buildExposedRevisions but keeps every revision that has a snapshot,
// including the current one. Used for link/pass shares so any revision — past
// or current — can be pinned as a frozen "snapshot" send. The Kitchen Table
// view filters out the current revision (it already shows it as "Latest").
export function buildExposedAll(
  revs: Revision[]
): NonNullable<SharedRecipe["exposedRevisions"]> {
  return revs
    .filter((r) => r.snapshot)
    .sort((a, b) => b.number - a.number)
    .map((r) => ({
      number: r.number,
      ...(r.note ? { note: r.note } : {}),
      ...(r.releaseName ? { releaseName: r.releaseName } : {}),
      ...(r.savedAt ? { savedAt: r.savedAt } : {}),
      ...(r.snapshot ? { snapshot: r.snapshot } : {}),
    }));
}

// Overwrite the share doc's exposedRevisions array. Used both at publish time
// (auto-populate with full history) and as a one-shot self-heal for share docs
// that pre-date this feature.
export async function setShareExposedRevisions(
  uid: string,
  recipeId: string,
  list: NonNullable<SharedRecipe["exposedRevisions"]>
): Promise<void> {
  const ref = doc(db, "sharedRecipes", `${uid}_${recipeId}`);
  await updateDoc(ref, { exposedRevisions: list, updatedAt: new Date().toISOString() });
}

// Toggle whether a specific revision is exposed on the public share doc.
// Requires the recipe to be published first (the share doc must exist).
export async function setRevisionExposed(
  uid: string,
  recipeId: string,
  rev: Revision,
  expose: boolean
): Promise<void> {
  const ref = doc(db, "sharedRecipes", `${uid}_${recipeId}`);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    throw new Error("Publish this recipe before promoting individual revisions.");
  }
  const data = snap.data() as SharedRecipe;
  const current = data.exposedRevisions ?? [];
  const without = current.filter((r) => r.number !== rev.number);
  const next = expose
    ? [
        ...without,
        {
          number: rev.number,
          ...(rev.note ? { note: rev.note } : {}),
          ...(rev.releaseName ? { releaseName: rev.releaseName } : {}),
          ...(rev.savedAt ? { savedAt: rev.savedAt } : {}),
          ...(rev.snapshot ? { snapshot: rev.snapshot } : {}),
        },
      ].sort((a, b) => a.number - b.number)
    : without;
  await updateDoc(ref, { exposedRevisions: next, updatedAt: new Date().toISOString() });
}

export async function forkSharedRecipe(uid: string, share: SharedRecipe, newId: string) {
  const recipeRef = doc(db, "users", uid, "recipes", newId);
  if ((await getDoc(recipeRef)).exists()) {
    throw new Error("A recipe with that id already exists in your cookbook.");
  }
  const snap: Partial<Recipe> = share.latestSnapshot ?? {};
  const forked = {
    ...withImage(snap, share.originalRecipeId),
    title: snap.title ?? share.title,
    forkedFrom: { ownerId: share.ownerId, shareId: share.shareId, originalId: share.originalRecipeId },
    forkedAt: new Date().toISOString(),
  };
  await setDoc(recipeRef, forked);
  await updateDoc(doc(db, "sharedRecipes", share.shareId), { forkCount: increment(1) });
  await setDoc(doc(db, "users", uid, "forkedRecipes", share.originalRecipeId), {
    shareId: share.shareId,
    forkedAt: new Date().toISOString(),
  });
}

// ── Pass (person-to-person sends + public links) ────────────────────

// Ensure a readable share doc exists so a recipe can back a public link or a
// pass. Reuses an existing share, preserving its visibility (a published recipe
// stays on the Kitchen Table); otherwise creates a private, link-only one.
// Always refreshes latestSnapshot + the full revision history so both the live
// head and any frozen revision resolve for viewers.
export async function ensureShareForLink(
  uid: string,
  recipe: Recipe,
  revs: Revision[],
  ownerName?: string
): Promise<string> {
  const shareId = `${uid}_${recipe.id}`;
  const ref = doc(db, "sharedRecipes", shareId);
  const existing = await getDoc(ref);
  const exposedRevisions = buildExposedAll(revs);
  if (existing.exists()) {
    await updateDoc(ref, {
      title: recipe.title,
      ...(ownerName ? { ownerName } : {}),
      ...(recipe.currentRevisionNumber != null
        ? { currentRevisionNumber: recipe.currentRevisionNumber }
        : {}),
      latestSnapshot: shareSnapshot(recipe),
      exposedRevisions,
      updatedAt: new Date().toISOString(),
    });
  } else {
    await setDoc(ref, {
      ownerId: uid,
      originalRecipeId: recipe.id,
      visibility: "private",
      title: recipe.title,
      ...(ownerName ? { ownerName } : {}),
      ...(recipe.subtitle ? { subtitle: recipe.subtitle } : {}),
      ...(recipe.description ? { description: recipe.description } : {}),
      ...(recipe.category ? { category: recipe.category } : {}),
      ...(recipe.tags && recipe.tags.length ? { tags: recipe.tags } : {}),
      ...(recipe.imageUrl ? { imageUrl: recipe.imageUrl } : {}),
      ...(recipe.macros ? { macros: recipe.macros } : {}),
      ...(recipe.currentRevisionNumber != null
        ? { currentRevisionNumber: recipe.currentRevisionNumber }
        : {}),
      latestSnapshot: shareSnapshot(recipe),
      exposedRevisions,
      forkCount: 0,
      commentCount: 0,
      publishedAt: new Date().toISOString(),
    });
  }
  return shareId;
}

// Serve a recipe to a friend. Ensures a share exists, then drops a pending
// RecipeSend into their "Served to You". mode "live" follows the dynamic head;
// "snapshot" pins revisionNumber forever. Rules enforce that the two are friends.
export async function passRecipe(
  uid: string,
  recipe: Recipe,
  revs: Revision[],
  opts: {
    toUid: string;
    toName?: string;
    mode: PassMode;
    revisionNumber?: number;
    message?: string;
    fromName?: string;
  }
): Promise<string> {
  const shareId = await ensureShareForLink(uid, recipe, revs, opts.fromName);
  const ref = await addDoc(collection(db, "recipeSends"), {
    fromUid: uid,
    ...(opts.fromName ? { fromName: opts.fromName } : {}),
    toUid: opts.toUid,
    ...(opts.toName ? { toName: opts.toName } : {}),
    shareId,
    mode: opts.mode,
    ...(opts.mode === "snapshot" && opts.revisionNumber != null
      ? { revisionNumber: opts.revisionNumber }
      : {}),
    title: recipe.title,
    ...(recipe.subtitle ? { subtitle: recipe.subtitle } : {}),
    ...(recipe.imageUrl ? { imageUrl: recipe.imageUrl } : {}),
    ...(opts.message ? { message: opts.message } : {}),
    sentAt: new Date().toISOString(),
    status: "pending",
  });
  return ref.id;
}

// Recipes served TO me. Sorted newest-first in memory to avoid a composite index.
export async function listIncomingPasses(uid: string): Promise<RecipeSend[]> {
  const snap = await getDocs(
    query(collection(db, "recipeSends"), where("toUid", "==", uid))
  );
  return snap.docs
    .map((d) => ({ sendId: d.id, ...(d.data() as Omit<RecipeSend, "sendId">) }))
    .sort((a, b) => (b.sentAt ?? "").localeCompare(a.sentAt ?? ""));
}

// Recipes I have passed to others.
export async function listSentPasses(uid: string): Promise<RecipeSend[]> {
  const snap = await getDocs(
    query(collection(db, "recipeSends"), where("fromUid", "==", uid))
  );
  return snap.docs
    .map((d) => ({ sendId: d.id, ...(d.data() as Omit<RecipeSend, "sendId">) }))
    .sort((a, b) => (b.sentAt ?? "").localeCompare(a.sentAt ?? ""));
}

export async function setPassStatus(
  sendId: string,
  status: RecipeSend["status"],
  toUid?: string
): Promise<void> {
  await updateDoc(doc(db, "recipeSends", sendId), {
    status,
    ...(toUid ? { toUid } : {}),
  });
}

// Sender revokes a pending pass.
export async function revokePass(sendId: string): Promise<void> {
  await deleteDoc(doc(db, "recipeSends", sendId));
}

// Live feed of recipes served to me, for the AppBar badge. Returns unsubscribe.
export function subscribeIncomingPasses(
  uid: string,
  cb: (sends: RecipeSend[]) => void
) {
  return onSnapshot(
    query(collection(db, "recipeSends"), where("toUid", "==", uid)),
    (snap) =>
      cb(snap.docs.map((d) => ({ sendId: d.id, ...(d.data() as Omit<RecipeSend, "sendId">) }))),
    () => cb([])
  );
}

// ── People directory (public profiles) ──────────────────────────────

// Upsert my searchable identity. Display name only — never email. Called on
// sign-in so others can find me to send a friend request.
export async function upsertPublicProfile(
  uid: string,
  displayName: string | null | undefined,
  photoURL?: string | null
): Promise<void> {
  const name = (displayName ?? "").trim() || "Anonymous cook";
  await setDoc(
    doc(db, "publicProfiles", uid),
    {
      uid,
      displayName: name,
      displayNameLower: name.toLowerCase(),
      ...(photoURL ? { photoURL } : {}),
    },
    { merge: true }
  );
}

// Prefix search over display names. Case-insensitive, capped. Firestore has no
// substring search, so this matches names that START with the query.
export async function searchProfiles(prefix: string): Promise<PublicProfile[]> {
  const p = prefix.trim().toLowerCase();
  if (!p) return [];
  const snap = await getDocs(
    query(
      collection(db, "publicProfiles"),
      orderBy("displayNameLower"),
      where("displayNameLower", ">=", p),
      where("displayNameLower", "<=", p + String.fromCodePoint(0xf8ff)),
      limit(20)
    )
  );
  return snap.docs.map((d) => d.data() as PublicProfile);
}

export async function getProfile(uid: string): Promise<PublicProfile | null> {
  const snap = await getDoc(doc(db, "publicProfiles", uid));
  return snap.exists() ? (snap.data() as PublicProfile) : null;
}

// ── Friend connections ──────────────────────────────────────────────

// One doc per relationship, id = sorted uid pair, so direction doesn't matter.
function connId(a: string, b: string): string {
  return a < b ? `${a}_${b}` : `${b}_${a}`;
}

// Send a friend request. If the other person already requested me, this accepts
// theirs instead of creating a duplicate. No-op if already friends/pending.
export async function sendFriendRequest(
  me: string,
  meName: string | null | undefined,
  other: string,
  otherName: string | null | undefined
): Promise<void> {
  if (me === other) throw new Error("You can't friend yourself.");
  const ref = doc(db, "connections", connId(me, other));
  const existing = await getDoc(ref);
  if (existing.exists()) {
    const c = existing.data() as Connection;
    if (c.status === "accepted") return;
    if (c.requestedBy !== me) {
      // They already asked — accept it.
      await updateDoc(ref, { status: "accepted", respondedAt: new Date().toISOString() });
    }
    return; // my own request is already pending
  }
  await setDoc(ref, {
    users: [me, other].sort(),
    requestedBy: me,
    status: "pending",
    names: { [me]: meName ?? "Anonymous cook", [other]: otherName ?? "Anonymous cook" },
    createdAt: new Date().toISOString(),
  });
}

// Accept (true) or decline (false) an incoming request. Decline removes the doc.
export async function respondToFriendRequest(
  connectionId: string,
  accept: boolean
): Promise<void> {
  const ref = doc(db, "connections", connectionId);
  if (accept) {
    await updateDoc(ref, { status: "accepted", respondedAt: new Date().toISOString() });
  } else {
    await deleteDoc(ref);
  }
}

// Cancel a pending request I sent, or unfriend an accepted connection.
export async function removeConnection(connectionId: string): Promise<void> {
  await deleteDoc(doc(db, "connections", connectionId));
}

export async function listConnections(uid: string): Promise<Connection[]> {
  const snap = await getDocs(
    query(collection(db, "connections"), where("users", "array-contains", uid))
  );
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Connection, "id">) }));
}

// Live feed of all my connections (friends + pending, both directions).
export function subscribeConnections(uid: string, cb: (cs: Connection[]) => void) {
  return onSnapshot(
    query(collection(db, "connections"), where("users", "array-contains", uid)),
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Connection, "id">) }))),
    () => cb([])
  );
}

// ── Connect (MCP) ───────────────────────────────────────────────────
export async function getOrCreateApiKey(uid: string): Promise<string> {
  const userRef = doc(db, "users", uid);
  const snap = await getDoc(userRef);
  const existing = snap.exists() ? (snap.data() as { apiKey?: string }).apiKey : undefined;
  if (existing) return existing;
  const k = crypto.randomUUID();
  await setDoc(userRef, { apiKey: k }, { merge: true });
  await setDoc(doc(db, "apiKeys", k), { uid });
  return k;
}

export async function rotateApiKey(uid: string): Promise<string> {
  const k = crypto.randomUUID();
  await setDoc(doc(db, "users", uid), { apiKey: k }, { merge: true });
  await setDoc(doc(db, "apiKeys", k), { uid });
  return k;
}

// Schema written by the MCP server. Daily/monthly counts are only valid when
// the accompanying date/period matches the current day/month.
export type UsageStats = {
  dailyCount?: number;
  dailyDate?: string;     // YYYY-MM-DD
  monthlyCount?: number;
  monthlyPeriod?: string; // YYYY-MM
  totalCount?: number;
  byTool?: Record<string, number>;
  lastCall?: string;      // ISO timestamp
};

export function subscribeUsage(uid: string, cb: (u: UsageStats | null) => void) {
  return onSnapshot(doc(db, "users", uid, "usageStats", "current"), (snap) => {
    cb(snap.exists() ? (snap.data() as UsageStats) : null);
  });
}

// Schema written by the chat service (chat/usage.py). Period fields are only
// valid when the accompanying date/period matches today/this week/this month;
// the chat service auto-resets when it rolls over.
export type ChatUsageStats = {
  weekStart?: string;                  // YYYY-Www (ISO week)
  weeklyNewRecipes?: number;           // cap: 3
  weeklyRevisions?: Record<string, number>; // recipeId -> count, cap: 10 each
  dailyTokens?: number;                // cap: 100k
  dailyDate?: string;                  // YYYY-MM-DD
  monthlyTokens?: number;              // cap: 2M
  monthlyPeriod?: string;              // YYYY-MM
  totalTokens?: number;
  totalGenerations?: number;
  lastCall?: string;                   // ISO timestamp
};

export function subscribeChatUsage(uid: string, cb: (u: ChatUsageStats | null) => void) {
  return onSnapshot(doc(db, "users", uid, "usageStats", "chat"), (snap) => {
    cb(snap.exists() ? (snap.data() as ChatUsageStats) : null);
  });
}
